<template>
  <StepLayout
    :on-save="onSave"
    :is-busy-on-save="isBusyOnSave"
    :on-next="onNext"
    :is-busy-on-next="isBusyOnNext"
    :on-back="onBack"
    :is-disabled="isDisabled"
    class="flex flex-col justify-between text-black tall:max-h-fullHeightWithChatBubble"
    step-navigation-button-data-ec="bulk_send_templates_page"
  >
    <template v-slot:title>
      {{ $t('bulkSend.steps.templates.title', { name }) }}
    </template>
    <p>{{ $t('bulkSend.steps.templates.description') }}</p>
    <div
      v-if="templates.length"
      class="mb-base md:flex md:items-baseline md:gap-base"
    >
      <p class="font-bold whitespace-nowrap">
        {{ $t('bulkSend.steps.templates.selected') }}
      </p>
      <ul class="flex flex-wrap p-0 m-0 list-none gap-base">
        <li
          v-for="template in templates"
          :key="template.id"
          class="flex items-stretch font-normal rounded-full bg-offWhite"
        >
          <p class="py-sm pl-twenty pr-0 m-0 whitespace-nowrap">
            {{ template.title }}
          </p>
          <button
            @click="() => handleRemove(template)"
            class="px-twenty border-none cursor-pointer background-none"
            data-ec="bulk_send_templates_page"
            data-et="remove_template_button"
          >
            <XMarkIcon />
          </button>
        </li>
      </ul>
    </div>

    <div
      class="flex flex-col flex-grow max-h-mostOfScreenHeight tall:max-h-none tall:min-h-0"
    >
      <VSearch
        @onSearch="handleSearch"
        @onResetSearch="handleResetSearch"
        :placeholder="$t('bulkSend.steps.templates.searchTemplates')"
        class="mb-base"
        data-ec="bulk_send_templates_page"
        data-et="template_search"
      />

      <DocumentList
        :loading="loadingTemplates"
        :list="templatesList"
        :value="templates"
        class="!p-1 !-m-1 !border-none !h-auto flex-shrink"
      />

      <NumberedPaginator
        v-if="meta"
        resource="templates"
        :meta="meta"
        @page="handlePagination"
        class="mt-base"
        page-data-ec="bulk_send_templates_page"
      />
    </div>
  </StepLayout>
</template>

<script>
import { computed, ref } from 'vue';
import { useGetters } from 'vuex-composition-helpers/dist';

import { useBulkSendStepsNavigation } from '@/composables/bulkSend/steps';
import useList from '@/composables/list/list';
import { ListTemplate } from '@/store/modules/bulkSend/models';

import VSearch from '@/components/deprecated/organisms/Search.vue';
import StepLayout from '@/components/ui/bulkSend/StepLayout.vue';
import NumberedPaginator from '@/components/ui/pagination/Paginator.vue';
import DocumentList from '@/views/components/DocumentList.vue';
import { XMarkIcon } from '@/components/icons/fontIcons';

export default {
  name: 'TemplatesPage',
  components: {
    StepLayout,
    VSearch,
    DocumentList,
    NumberedPaginator,
    XMarkIcon,
  },
  setup() {
    const { bulkSend } = useGetters({ bulkSend: 'bulkSend/bulkSend' });
    const name = computed(() => bulkSend.value.name);

    const [fetchTemplates, templatesList, meta, loadingTemplates] = useList(
      'templates',
      ListTemplate,
    );

    // initialise local state, spread array to break link with store
    const templates = ref([...bulkSend.value.templates]);

    const updateValue = computed(() =>
      templates.value.map(template => template.id));
    const isValid = computed(() => !!templates.value?.length);

    const {
      onNext,
      isBusyOnNext,
      onSave,
      isBusyOnSave,
      onBack,
      isDisabled,
    } = useBulkSendStepsNavigation(
      'bulkSend/updateTemplates',
      updateValue,
      isValid,
    );

    return {
      name,
      templates,
      fetchTemplates,
      templatesList,
      meta,
      loadingTemplates,
      onNext,
      isBusyOnNext,
      onSave,
      isBusyOnSave,
      onBack,
      isDisabled,
    };
  },
  created() {
    this.fetchTemplates({ limit: 9 });
  },
  methods: {
    handleSearch({ value }) {
      this.fetchTemplates({ limit: 9, query: value });
    },
    handleResetSearch() {
      this.fetchTemplates({ limit: 9 });
    },
    handlePagination(url) {
      const parsedUrl = new URL(url);
      const page = parsedUrl.searchParams.get('page');

      this.fetchTemplates({
        limit: 9,
        offset: (page - 1) * 9,
        ...Object.fromEntries(parsedUrl.searchParams),
      });
    },
    handleRemove(template) {
      const index = this.templates.findIndex(item => template.fingerprint === item.fingerprint);

      if (index > -1) {
        this.templates.splice(index, 1);
      }
    },
  },
};
</script>

<style scoped>
.background-none {
  background: none;
}
</style>
